import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useInvoices, useInvoice } from "@/hooks/useInvoices";
import { useApiMutation, useApi } from "@/lib/api";
import { useToast } from "@/hooks/useToast";
import { BillingPeriod } from "@/types/billing";
import { TenantList } from "./TenantList";
import { EmailPreview } from "./EmailPreview";

interface EmailConfig { sender_email: string | null; receiver_email: string | null; }

export function NotifyingView({ period, refetch, location = null }: { period: BillingPeriod; refetch: () => void; location?: string | null }) {
  const { data: invoices, refetch: refetchInvoices, loading } = useInvoices(period.period_id);
  const [selected, setSelected] = useState<string | null>(null);
  const { data: invoice, refetch: refetchOne } = useInvoice(period.period_id, selected);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [sendAllOpen, setSendAllOpen] = useState(false);
  const { mutate, loading: mutating } = useApiMutation();
  const { push } = useToast();
  const { data: emailConfig } = useApi<EmailConfig>("/api/notification/config");

  const visibleInvoices = location ? (invoices?.filter((i) => i.location === location) ?? []) : (invoices ?? []);

  useEffect(() => {
    if (invoices && invoices.length > 0) {
      const inView = location ? invoices.filter((i) => i.location === location) : invoices;
      if (!selected || !inView.find((i) => i.agreement_id === selected)) {
        setSelected(inView[0]?.agreement_id ?? null);
      }
    }
  }, [invoices, location]);

  // Backend only blocks reopen when literally everything is sent (nothing
  // left to revert) — matches routes.billing.reopen_invoicing.
  const allSent = (invoices?.length ?? 0) > 0 && invoices!.every((i) => i.status === "sent");
  const sentCount = invoices?.filter((i) => i.status === "sent").length ?? 0;

  async function sendOne(agreementId: string, force = false) {
    const url = `/api/notification/${period.period_id}/send/${agreementId}${force ? "?force=true" : ""}`;
    try {
      await mutate(url, "POST");
      push({ message: force ? "Wysłano ponownie" : "Wysłano", tone: "success" });
      refetchInvoices();
      if (agreementId === selected) refetchOne();
      refetch();
    } catch (err) {
      push({ message: `Błąd wysyłki: ${(err as Error).message}`, tone: "danger" });
    }
  }

  async function sendAll() {
    try {
      await mutate(`/api/notification/${period.period_id}/send`, "POST");
      push({ message: "Wysłano wszystkie", tone: "success" });
      setSendAllOpen(false);
      refetchInvoices();
      refetch();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  async function saveNote(agreementId: string, message: string) {
    await mutate(`/api/invoice/${period.period_id}/${agreementId}/notification-message`, "PATCH", { message });
    refetchInvoices();
    if (agreementId === selected) refetchOne();
  }

  async function reopenInvoicing() {
    try {
      await mutate("/api/billing/period/reopen-invoicing", "POST");
      push({ message: "Wrócono do faktur", tone: "success" });
      setReopenOpen(false);
      refetch();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  if (loading || !invoices) return <Skeleton className="h-64 w-full" />;

  return (
    <>
      <div className="flex flex-wrap justify-end gap-2 mb-4">
        {!allSent && (
          <Button variant="secondary" onClick={() => setReopenOpen(true)}>Wróć do faktur ◀</Button>
        )}
        <Button iconLeft={<Send className="size-3.5" />} onClick={() => setSendAllOpen(true)}>
          Wyślij wszystkim ✉
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <TenantList
          invoices={visibleInvoices}
          selectedId={selected}
          onSelect={setSelected}
          showStatus
        />
        <div className="flex-1 min-w-0">
          {invoice ? (
            <EmailPreview
              invoice={invoice}
              senderEmail={emailConfig?.sender_email ?? undefined}
              receiverEmail={emailConfig?.receiver_email ?? undefined}
              onSend={() => sendOne(invoice.agreement_id)}
              onResend={() => sendOne(invoice.agreement_id, true)}
              onSaveNote={(message) => saveNote(invoice.agreement_id, message)}
              sending={mutating}
            />
          ) : (
            <Skeleton className="h-64 w-full" />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={sendAllOpen}
        onClose={() => setSendAllOpen(false)}
        onConfirm={sendAll}
        title="Wysłać wszystkie?"
        confirmLabel="Wyślij"
        loading={mutating}
        description={`Zostanie wysłanych ${invoices.filter((i) => i.status !== "sent").length} powiadomień.`}
      />
      <ConfirmDialog
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
        onConfirm={reopenInvoicing}
        title="Wrócić do faktur?"
        confirmLabel="Wróć"
        description={
          sentCount > 0
            ? `${sentCount} ${sentCount === 1 ? "faktura już wysłana zostanie" : "faktur już wysłanych zostanie"} bez zmian. Pozostałe wrócą do statusu szkicu.`
            : "Wszystkie faktury wrócą do statusu szkicu."
        }
      />
    </>
  );
}
